import { all, call, put, takeLatest, delay } from 'redux-saga/effects'
import AudioActions, { AudioTypes } from 'Redux/AudioRedux'

// Create Audio
export function* createAudio(api, action) {
  try {
    const { scriptId, options } = action
    const params = options?.voiceId ? {
      voice_id: options.voiceId,
      voice_name: options.voiceName || null,
    } : null

    const resp = yield call(api.createAudio, scriptId, params)

    if (resp.ok) {
      yield put(AudioActions.audioCreateSuccess(resp.data))
      if (action.callback) {
        action.callback(resp.data, null)
      }
    } else {
      const error = resp.data?.error || 'Failed to create audio'
      yield put(AudioActions.audioCreateFailure(error))
      if (action.callback) {
        action.callback(null, error)
      }
    }
  } catch (error) {
    const errorMsg = error.message || 'Failed to create audio'
    yield put(AudioActions.audioCreateFailure(errorMsg))
    if (action.callback) {
      action.callback(null, errorMsg)
    }
  }
}

// Get Audio
export function* getAudio(api, action) {
  try {
    const resp = yield call(api.getAudio, action.id)

    if (resp.ok) {
      yield put(AudioActions.audioGetSuccess(resp.data))
      if (action.callback) {
        action.callback(resp.data, null)
      }
    } else {
      const error = resp.data?.error || 'Failed to get audio'
      yield put(AudioActions.audioGetFailure(error))
      if (action.callback) {
        action.callback(null, error)
      }
    }
  } catch (error) {
    const errorMsg = error.message || 'Failed to get audio'
    yield put(AudioActions.audioGetFailure(errorMsg))
    if (action.callback) {
      action.callback(null, errorMsg)
    }
  }
}

// Get Audio Status
export function* getAudioStatus(api, action) {
  try {
    const resp = yield call(api.getAudioStatus, action.id)

    if (resp.ok) {
      yield put(AudioActions.audioStatusSuccess(resp.data))
      if (action.callback) {
        action.callback(resp.data, null)
      }
    } else {
      const error = resp.data?.error || 'Failed to get audio status'
      yield put(AudioActions.audioStatusFailure(error))
      if (action.callback) {
        action.callback(null, error)
      }
    }
  } catch (error) {
    const errorMsg = error.message || 'Failed to get audio status'
    yield put(AudioActions.audioStatusFailure(errorMsg))
    if (action.callback) {
      action.callback(null, errorMsg)
    }
  }
}

// List Audios for Script
export function* listAudios(api, action) {
  try {
    const resp = yield call(api.getAudiosForScript, action.scriptId)

    if (resp.ok) {
      yield put(AudioActions.audioListSuccess(resp.data))
      if (action.callback) {
        action.callback(resp.data, null)
      }
    } else {
      const error = resp.data?.error || 'Failed to list audios'
      yield put(AudioActions.audioListFailure(error))
      if (action.callback) {
        action.callback(null, error)
      }
    }
  } catch (error) {
    const errorMsg = error.message || 'Failed to list audios'
    yield put(AudioActions.audioListFailure(errorMsg))
    if (action.callback) {
      action.callback(null, errorMsg)
    }
  }
}

// Poll Audio Until Complete
export function* pollAudio(api, action) {
  const { id, interval: pollInterval = 10000, callback } = action
  const maxAttempts = 120 // 20 minutes max at 10s interval
  
  let attempts = 0
  
  try {
    while (attempts < maxAttempts) {
      const resp = yield call(api.getAudioStatus, id)
      
      if (!resp.ok) {
        const error = resp.data?.error || 'Failed to get audio status'
        yield put(AudioActions.audioPollFailure(error))
        if (callback) {
          callback(null, error)
        }
        return
      }
      
      const status = resp.data
      yield put(AudioActions.audioPollProgress(status))
      
      if (status.status === 'completed') {
        // Fetch full audio data
        const fullResp = yield call(api.getAudio, id)
        if (fullResp.ok) {
          yield put(AudioActions.audioPollSuccess(fullResp.data))
          if (callback) {
            callback(fullResp.data, null)
          }
        } else {
          if (callback) {
            callback(null, 'Failed to load completed audio')
          }
        }
        return
      }
      
      if (status.status === 'failed') {
        const error = status.error_message || 'Audio generation failed'
        yield put(AudioActions.audioPollFailure(error))
        if (callback) {
          callback(null, error)
        }
        return
      }
      
      attempts++
      yield delay(pollInterval)
    }
    
    // Timeout
    const error = 'Audio generation timed out'
    yield put(AudioActions.audioPollFailure(error))
    if (callback) {
      callback(null, error)
    }
  } catch (error) {
    const errorMsg = error.message || 'Polling failed'
    yield put(AudioActions.audioPollFailure(errorMsg))
    if (callback) {
      callback(null, errorMsg)
    }
  }
}

// Generate Audio (create + poll)
export function* generateAudio(api, action) {
  const { scriptId, options, callback } = action
  
  try {
    // Create audio
    const params = options?.voiceId ? {
      voice_id: options.voiceId,
      voice_name: options.voiceName || null,
    } : null
    
    const createResp = yield call(api.createAudio, scriptId, params)
    
    if (!createResp.ok) {
      const error = createResp.data?.error || 'Failed to create audio'
      yield put(AudioActions.audioGenerateFailure(error))
      if (callback) {
        callback(null, error)
      }
      return
    }
    
    const audioId = createResp.data.id
    
    // Poll until complete
    const interval = 10000 // 10 seconds
    const maxAttempts = 120
    let attempts = 0
    
    while (attempts < maxAttempts) {
      const statusResp = yield call(api.getAudioStatus, audioId)
      
      if (!statusResp.ok) {
        const error = statusResp.data?.error || 'Failed to get audio status'
        yield put(AudioActions.audioGenerateFailure(error))
        if (callback) {
          callback(null, error)
        }
        return
      }
      
      const status = statusResp.data
      yield put(AudioActions.audioGenerateProgress(status))
      
      if (status.status === 'completed') {
        const fullResp = yield call(api.getAudio, audioId)
        if (fullResp.ok) {
          yield put(AudioActions.audioGenerateSuccess(fullResp.data))
          if (callback) {
            callback(fullResp.data, null)
          }
        } else {
          const error = 'Failed to load completed audio'
          yield put(AudioActions.audioGenerateFailure(error))
          if (callback) {
            callback(null, error)
          }
        }
        return
      }
      
      if (status.status === 'failed') {
        const error = status.error_message || 'Audio generation failed'
        yield put(AudioActions.audioGenerateFailure(error))
        if (callback) {
          callback(null, error)
        }
        return
      }
      
      attempts++
      yield delay(interval)
    }
    
    // Timeout
    const error = 'Audio generation timed out'
    yield put(AudioActions.audioGenerateFailure(error))
    if (callback) {
      callback(null, error)
    }
  } catch (error) {
    const errorMsg = error.message || 'Audio generation failed'
    yield put(AudioActions.audioGenerateFailure(errorMsg))
    if (callback) {
      callback(null, errorMsg)
    }
  }
}

// Watcher saga
export default function* audioSaga(api) {
  yield all([
    takeLatest(AudioTypes.AUDIO_CREATE_REQUEST, createAudio, api),
    takeLatest(AudioTypes.AUDIO_GET_REQUEST, getAudio, api),
    takeLatest(AudioTypes.AUDIO_STATUS_REQUEST, getAudioStatus, api),
    takeLatest(AudioTypes.AUDIO_LIST_REQUEST, listAudios, api),
    takeLatest(AudioTypes.AUDIO_POLL_REQUEST, pollAudio, api),
    takeLatest(AudioTypes.AUDIO_GENERATE_REQUEST, generateAudio, api),
  ])
}
